/** Grouping for a Progress / New-Info qualitative block — the ONE implementation, shared by both
 * renderers of that value (bounded-emission design §4.3, "S3 grouped summaries").
 *
 * There are two surfaces that paint the same items: `CrmRecentUpdates` (the native Recent-Updates
 * cluster) and `CrmRecordPanel`'s `BlockValue` (the Fields grid, on BOTH the proposed and the
 * committed row). They diverged — Recent Updates grew sub-headers while `BlockValue` still projected
 * `.text` into a flat `<li>` run — so a grouped item rendered grouped in one place on a record and
 * flat in another, on the same screen. Extracted for the same reason `compareRoleGroups` is: a second
 * copy of a grouping rule is a difference the operator SEES.
 *
 * ⚠ A `group` is FREE TEXT and is never validated against a vocabulary (owner ruling 2026-08-09 — the
 * six groups in the aggregator prompt are examples, not an enum). A group the model coined for this
 * one deal renders exactly as written.
 *
 * ⚠ MIXED grouped/ungrouped is the COMMON case, not an edge: items written before the S2 prompt went
 * live (2026-08-10) never gain a `group`, so a long-running deal carries both. Ungrouped items keep
 * their own headerless run at the position where the first of them appeared.
 */

/** One bullet in a run: its render text plus the stable identity the payload carried, if any. */
export interface BlockItemRun {
  /** S5a-2 `item_id` (a content hash of the normalized text, never model-supplied), or `null` for a
   * legacy bare-string / object-without-item_id bullet. */
  itemId: string | null;
  text: string;
}

/** One rendered run: a free-text sub-header (or `null` for the headerless run) + its bullet texts. */
export interface BlockGroup {
  group: string | null;
  texts: string[];
  /** The same bullets, same order, carrying identity.
   *
   * `texts` is the RENDER projection of this list, and both are built in ONE pass below so they
   * cannot disagree (pinned by test). Two representations exist because the three renderers that
   * consume `texts` need no identity, while edit-and-accept — which must NAME the bullet it drops —
   * does, and widening `texts` itself would rewrite `CrmBlockRuns`, `BlockValue` and the timeline
   * merge plus their class-stripped goldens for no gain on those surfaces.
   *
   * ⚠ An `itemId: null` bullet is NOT individually addressable: `item_id` is what the edit-accept
   * payload filters on, so a caller must disable its control rather than guess an index. Legacy
   * bare-string payloads are still live in prod (backend `normalize_block_items` documents them as
   * supported), so this is the ordinary case on a long-running deal, not an edge. */
  items: BlockItemRun[];
}

/** The redundant section label a stored bullet may open with, stripped at DISPLAY only.
 *
 * Owner ruling 2026-08-20: the section header on every surface already reads PROGRESS or NEW INFO, so
 * a bullet that also opens `"Progress: "` says it twice. The label is in the STORED text because the
 * aggregator prompt asks the model for it (the `<Progress: ...>` / `<New Info: ...>` schema hints), so
 * months of history carry it and no prompt change can retroactively clean them — hence a display strip,
 * the same discipline as the thread parser and the twin-suppression matcher: **stored content is never
 * rewritten.** Removing the ask from the prompt is a separate, forward-only change.
 *
 * ⚠ THE ANCHOR AND THE COLON ARE BOTH LOAD-BEARING, and each one alone is a false positive waiting to
 * happen. Between them they are the whole reason this is a pinned regex and not a `.replace()`:
 *
 *   · `^` with NO `m` flag ⇒ position 0 of the (already trimmed) text, once. A bullet that mentions
 *     progress mid-sentence keeps every word of it. An unanchored strip would eat the label out of the
 *     middle of a sentence, which is silent corruption of a deal narrative — pinned by the
 *     "mid-text" and "unanchored" tests, and killed by the mutation harness.
 *   · the colon must follow the label IMMEDIATELY, so `"Progressive Insurance renewed…"` is untouched:
 *     `progress` is there at position 0, but what follows is `ive`, not `:`. This is the obvious way to
 *     get the whole change wrong, and it is the first thing to re-check if anyone widens the pattern.
 *   · `new\s+info:` cannot swallow `"New Information: …"` for the same reason — after `info` comes
 *     `rmation`, not the colon.
 *
 * ⚠ ONLY THESE TWO LABELS, never a general "drop anything before the first colon". A bullet reading
 * `"Lockton: sent the tracker"` opens with a counterparty name, and a generic rule would delete it.
 * Case and the run of whitespace after the colon vary because the model is not byte-consistent
 * (`"Progress:"`, `"progress: "`, `"New info:"`, `"New Info:  "` all appear), so those are tolerated —
 * the label spelling is not. */
const REDUNDANT_BLOCK_LABEL = /^(?:progress|new\s+info):\s*/i;

/** Strip the redundant label — UNLESS that would empty the bullet, in which case keep it whole.
 *
 * ⛔ THE FALLBACK IS THE WHOLE POINT OF THIS FUNCTION, and deleting it looks like a simplification.
 * A bullet that is *only* the label is real: the model occasionally emits `"Progress:"` with nothing
 * after it. Reducing it to `''` drops the item, and dropping the last item of a block empties the
 * block — which is not a cosmetic loss, it is a DISAPPEARANCE two levels up:
 *
 *   · `recordTimelineMerge.updatesLaneItems` skips a version whose value yields no groups, so an
 *     ACCEPTED entry vanishes from the record's history — the operator loses the audit row, not just
 *     a bullet.
 *   · `CrmRecentUpdates`' visibility filter keys on `blockHasContent`, so the settled block (or the
 *     whole cluster) stops rendering.
 *
 * `pendingItems` is deliberately immune — it keeps a contentless proposal so it stays dismissible —
 * but these two paths are not, and patching them individually would leave the next consumer exposed.
 * Holding the invariant HERE removes the entire class at one seam.
 *
 * So the trade is made on purpose and it is this codebase's standing rule: an ugly bare `"Progress:"`
 * bullet is a VISIBLE ODDITY, and a missing history row is HIDDEN CONTENT. Fail toward the oddity,
 * every time. (A genuinely blank `text` still yields `''` and is still dropped — there was never any
 * content there to lose.) */
function stripRedundantLabel(trimmed: string): string {
  const stripped = trimmed.replace(REDUNDANT_BLOCK_LABEL, '');
  return stripped || trimmed;
}

/** The item's display text, or `''` for anything unrenderable.
 *
 * Accepts a legacy BARE STRING as well as the `{item_id, text, evidence_event_ids, group,
 * materiality}` shape the writer emits (`proposal_evidence.normalize_block_items`) — both live in
 * prod, and `normalize_block_items` documents the string form as supported for exactly that reason.
 *
 * ⚠ NON-STRING `text` IS DROPPED, NEVER COERCED, and this is the line the whole renderer exists to
 * hold. `String(t).trim()` would render `text: 5` as the narrative "5" and `text: {}` as
 * "[object Object]" — the exact string whose appearance on the deal record is why the `panel` surface
 * stayed closed until a real block renderer existed. Pinned by the "drops a non-string text rather
 * than coercing it" test; a swap to `String(t)` must go red.
 *
 * The label strip runs here, on the trimmed string, for BOTH item shapes, via
 * `stripRedundantLabel` — which never returns `''` for a bullet that had content, so this function
 * cannot turn a real item into a dropped one (see the disappearance note there). Stripping downstream
 * of the non-string guard is deliberate: a non-string `text` is still dropped, never stringified. */
function itemText(item: unknown): string {
  if (typeof item === 'string') return stripRedundantLabel(item.trim());
  const t = (item as { text?: unknown } | null | undefined)?.text;
  return typeof t === 'string' ? stripRedundantLabel(t.trim()) : '';
}

/** Mirrors backend `proposal_evidence.item_group`: a non-blank string `group`, else `null`. A blank
 * or whitespace-only group is NOT a group — it must not render as an empty sub-header. */
function itemGroup(item: unknown): string | null {
  if (typeof item === 'string') return null;
  const g = (item as { group?: unknown } | null | undefined)?.group;
  return typeof g === 'string' && g.trim() !== '' ? g.trim() : null;
}

/** The bullet's stable `item_id`, or `null` when the payload carries none.
 *
 * A NON-BLANK STRING ONLY, and never derived. The temptation is to fall back to the item's index or a
 * hash of the text so every bullet is addressable — both are wrong for the one consumer that needs
 * this: edit-and-accept filters the RAW payload on `item_id`, so a synthesized id names nothing and
 * would silently drop no bullet (index) or the wrong one (a hash that disagrees with the writer's).
 * `null` makes that impossible, at the price of one disabled control. */
function itemId(item: unknown): string | null {
  if (typeof item === 'string') return null;
  const id = (item as { item_id?: unknown } | null | undefined)?.item_id;
  return typeof id === 'string' && id.trim() !== '' ? id : null;
}

/**
 * Group a block value into renderable runs, in FIRST-APPEARANCE order.
 *
 * When ANY item carries a group, the result is one run per distinct group (repeats collect under the
 * run their group first opened). When NONE does — every payload written before S2 — the result is
 * exactly ONE run whose `group` is `null`. An empty / non-array / all-blank value yields `[]`.
 *
 * ⚠ THAT ONE-NULL-RUN PROPERTY IS LOAD-BEARING but it is NOT the compatibility guarantee, and the
 * difference matters because it is how the real mechanism gets refactored away. This function cannot
 * preserve anybody's DOM; it returns data. The guarantee that a pre-S2 block renders the byte-identical
 * flat list lives in ONE place — `BlockValue`'s early return in `CrmRecordPanel.tsx`, which keys on
 * exactly this property (`length === 1 && group === null`) and is pinned by the class-stripped goldens
 * in `crmBlockGroups.test.tsx`. An earlier cut of this function carried a special-cased "collapse"
 * branch for the ungrouped case that LOOKED like the mechanism; it was provably identical to the
 * ordinary path for every input, so it was deleted rather than left to mislead.
 *
 * `value` is deliberately `unknown`: the panel receives a raw `value_json` cell, the Recent-Updates
 * cluster a typed `CrmBlockItem[]`, the timeline merge either. Narrowing the parameter would force a
 * call site into a cast, which is how the copies drifted in the first place.
 */
export function groupBlockItems(value: unknown): BlockGroup[] {
  const order: (string | null)[] = [];
  const byGroup = new Map<string | null, BlockItemRun[]>();
  for (const item of Array.isArray(value) ? value : []) {
    const text = itemText(item);
    if (!text) continue;
    const group = itemGroup(item);
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      order.push(group);
    }
    byGroup.get(group)!.push({ itemId: itemId(item), text });
  }
  // ONE pass, so `texts` is `items` by construction rather than by a second traversal that could
  // filter differently. Anything that drops a bullet (the `!text` guard, above) drops it from both.
  return order.map((g) => {
    const items = byGroup.get(g) ?? [];
    return { group: g, texts: items.map((it) => it.text), items };
  });
}

/** Does this block have anything renderable in it?
 *
 * A PREDICATE, not a text list, on purpose. This started life as `blockBulletTexts`, returning every
 * text with grouping collapsed — but that flattens in GROUP order, not document order, and the name
 * promised neither. Both callers only ever asked "is there anything here" (`.length > 0`), so a future
 * caller adopting it to RENDER would have silently reordered a deal's narrative. Returning a boolean
 * removes the trap instead of documenting it.
 *
 * Routed through `groupBlockItems` so "renderable" cannot mean two different things on two surfaces —
 * a block that renders as `—` must never come with live Accept / Dismiss buttons beside it. */
export function blockHasContent(value: unknown): boolean {
  return groupBlockItems(value).length > 0;
}

/** A block value with the named items removed — the payload for `review-edit-accept`.
 *
 * ⛔ THE SURVIVORS ARE THE STORED OBJECTS, BY REFERENCE. Never rebuild them from `groupBlockItems`:
 * that projection strips the redundant `"New Info: "` label, trims, and carries neither
 * `evidence_event_ids` nor `group` / `materiality` / `supersedes`. Publishing it would rewrite every
 * surviving bullet's text and destroy the lineage that makes a bullet auditable — the exact property
 * the incident that prompted this affordance was about.
 *
 * An item with no `item_id` can never be in `dropped` (its control is disabled), so it is always kept.
 * That is the safe direction: the remedy for an unaddressable bullet is Reject, never the silent
 * removal of a neighbour.
 *
 * Lives HERE, beside the grouping rule, because BOTH block renderers need it — the Recent-Updates
 * cluster (v1 path) and the record panel's v2 field row. It was local to the cluster for one commit
 * and the second surface needed it immediately. */
export function editedBlockValue(value: unknown, dropped: ReadonlySet<string>): unknown[] {
  return (Array.isArray(value) ? value : []).filter((it) => {
    const id = (it as { item_id?: unknown } | null)?.item_id;
    return !(typeof id === 'string' && dropped.has(id));
  });
}
